import * as THREE from 'three'
import { auditMeshPartOwnership, classifyMeshPart, type MeshPartDescriptor } from './MeshPartOwnership'

export interface RuntimeMeshOwnershipReport {
  identity: string
  audit: ReturnType<typeof auditMeshPartOwnership>
  isolatedMaterialCount: number
  isolatedGeometryCount: number
}

function materialNames(material: THREE.Material | THREE.Material[]): string[] {
  const list = Array.isArray(material) ? material : [material]
  return list.map((m) => m?.name || m?.type || 'material')
}

export function applyMeshPartOwnership(
  root: THREE.Object3D,
  identity: string,
): RuntimeMeshOwnershipReport {
  const cached = root.userData.__meshPartOwnershipReport as RuntimeMeshOwnershipReport | undefined
  if (cached) return cached

  const rows: Array<{
    mesh: THREE.Mesh
    descriptor: MeshPartDescriptor
    classification: ReturnType<typeof classifyMeshPart>
    originalGeometry: THREE.BufferGeometry
  }> = []

  root.traverse((node) => {
    const mesh = node as THREE.Mesh
    if (!mesh.isMesh || !mesh.geometry) return
    const skinned = Boolean((mesh as THREE.SkinnedMesh).isSkinnedMesh)
    const descriptor: MeshPartDescriptor = {
      nodeName: mesh.name || 'unnamed-mesh',
      materialNames: materialNames(mesh.material),
      skinned,
      boneCount: skinned ? ((mesh as THREE.SkinnedMesh).skeleton?.bones?.length ?? 0) : 0,
      morphTargetCount: Object.keys(mesh.morphTargetDictionary ?? {}).length,
      vertexCount: mesh.geometry.attributes.position?.count ?? 0,
    }
    rows.push({
      mesh,
      descriptor,
      classification: classifyMeshPart(descriptor),
      originalGeometry: mesh.geometry,
    })
  })

  // A character exported as one SkinnedMesh is one presentation surface,
  // even when its material name says "armor" or "cloth". Treat semantic gear
  // signals on that sole skinned mesh as monolithic-mixed and require an
  // offline derived split; runtime detach is forbidden.
  if (rows.length === 1 && rows[0].descriptor.skinned) {
    rows[0].descriptor.soleSkinnedPresentation = true
    rows[0].classification = classifyMeshPart(rows[0].descriptor)
  }

  const geometryKinds = new Map<THREE.BufferGeometry, Set<string>>()
  for (const row of rows) {
    let kinds = geometryKinds.get(row.originalGeometry)
    if (!kinds) {
      kinds = new Set<string>()
      geometryKinds.set(row.originalGeometry, kinds)
    }
    kinds.add(row.classification.kind)
  }

  let isolatedMaterialCount = 0
  let isolatedGeometryCount = 0

  for (const row of rows) {
    const { mesh, classification, originalGeometry } = row

    // Per-part material ownership prevents tint/roughness/dissolve/damage
    // mutations on one visual surface from leaking into another body/garment.
    if (!mesh.userData.__materialOwnershipIsolated) {
      mesh.material = Array.isArray(mesh.material)
        ? mesh.material.map((m) => m.clone())
        : mesh.material.clone()
      mesh.userData.__materialOwnershipIsolated = true
      isolatedMaterialCount++
    }

    // Geometry only needs duplication when the same BufferGeometry instance is
    // being reused by semantically different parts. Skinned geometry remains
    // bound to the same skeleton; attributes are cloned, bind pose is untouched.
    const kinds = geometryKinds.get(originalGeometry)
    if (kinds && kinds.size > 1 && !mesh.userData.__geometryOwnershipIsolated) {
      mesh.geometry = originalGeometry.clone()
      mesh.userData.__geometryOwnershipIsolated = true
      isolatedGeometryCount++
    }

    mesh.userData.__meshPartKind = classification.kind
    mesh.userData.__meshPartPhysics = classification.physics
    mesh.userData.__meshPartConfidence = classification.confidence
    mesh.userData.__meshPartIndependent = classification.safeForIndependentTransform
    mesh.userData.__meshPartRequiresDerivedSplit = classification.requiresDerivedSplit
    mesh.userData.__meshPartOwner = identity
  }

  const audit = auditMeshPartOwnership(rows.map((row) => row.descriptor))
  const report: RuntimeMeshOwnershipReport = {
    identity,
    audit,
    isolatedMaterialCount,
    isolatedGeometryCount,
  }
  root.userData.__meshPartOwnershipReport = report
  root.userData.__meshPartOwnershipApplied = true
  root.userData.__meshPartDerivedSplitRequired = audit.derivedSplitRequired
  return report
}
